import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardHeader from "../../layouts/dashboard_header";
import SideMenu from "../../layouts/dashboard_menu";
import DashboardFooter from "../../layouts/dashboard_footer";
import { useBaseUrl } from '../../../utils/utils';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faHandshake, faVideo, faFile, faTasks, faUsers, faCalendarAlt,
    faChartLine, faDollarSign, faClock, faCheckCircle, faExclamationTriangle,
    faPlus, faSearch, faFilter, faCog, faBell, faEnvelope, faPhone,
    faHeartbeat, faShieldAlt, faSignature, faDownload, faUpload, faEye, faFileContract
} from '@fortawesome/free-solid-svg-icons';
import TelehealthInterface from './TelehealthInterface';
import DocumentManagement from './DocumentManagement';
import TaskManagement from './TaskManagement';
import ContractsPanel from './ContractsPanel';

interface Collaboration {
    collaboration_id: string;
    title: string;
    description: string;
    status: string;
    collaboration_type: string;
    patient: {
        id: number;
        name: string;
        contact: string;
        email: string;
    };
    created_by: string;
    created_at: string;
    start_date: string;
    end_date: string;
    progress_percentage: number;
    billing: {
        total_amount: number;
        currency: string;
        payment_status: string;
        billing_mode: string;
        invoice_generated: boolean;
        invoice_number?: string;
    };
    stats: {
        tasks_count: number;
        completed_tasks: number;
        documents_count: number;
        telehealth_sessions: number;
        total_hours: number;
        pending_approvals: number;
    };
}

interface Props {
    onClose?: () => void;
}

const CollaborationsCenter: React.FC<Props> = ({ onClose }) => {
    const baseUrl = useBaseUrl();
    const [collaborations, setCollaborations] = useState<Collaboration[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedCollaboration, setSelectedCollaboration] = useState<Collaboration | null>(null);
    const [activeView, setActiveView] = useState<'overview' | 'telehealth' | 'documents' | 'tasks' | 'contracts' | 'details'>('overview');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [filterType, setFilterType] = useState<string>('all');
    const [apiError, setApiError] = useState<string | null>(null);

    useEffect(() => {
        fetchCollaborations();
    }, []);

    const fetchCollaborations = async () => {
        setLoading(true);
        try {
            // Get authentication token from localStorage
            const token = localStorage.getItem('token') || localStorage.getItem('authToken');
            
            const response = await axios.get(`${baseUrl}collaborations/collaborations/`, {
                headers: {
                    'Authorization': token ? `Bearer ${token}` : '',
                    'Content-Type': 'application/json',
                }
            });
            
            if (response.data && response.data.status === 'success') {
                setCollaborations(response.data.data || []);
            } else {
                // Handle case where response doesn't have expected structure
                console.warn('Unexpected response structure:', response.data);
                setCollaborations([]);
            }
        } catch (error: any) {
            console.error('Error fetching collaborations:', error);
            
            let errorMessage = 'Failed to fetch collaborations';
            if (error.response) {
                // Server responded with error status
                if (error.response.status === 401) {
                    errorMessage = 'Authentication required. Please log in again.';
                } else if (error.response.status === 403) {
                    errorMessage = 'Access denied. You do not have permission to view collaborations.';
                } else if (error.response.status === 404) {
                    errorMessage = 'Collaborations endpoint not found. Please check the API configuration.';
                } else if (error.response.status >= 500) {
                    errorMessage = 'Server error. Please try again later.';
                } else {
                    errorMessage = `Server error: ${error.response.status} - ${error.response.statusText}`;
                }
            } else if (error.request) {
                // Network error - no response received
                errorMessage = 'Network error. Please check your connection and try again.';
            }
            
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: errorMessage,
                footer: 'If this problem persists, please contact support.'
            });
            
            // Set empty array to prevent further errors
            setCollaborations([]);
            setApiError(errorMessage);
        } finally {
            setLoading(false);
        }
    };





    const getStatusBadge = (status: string) => {
        const statusColors = {
            'active': 'bg-success',
            'completed': 'bg-primary',
            'cancelled': 'bg-danger',
            'on_hold': 'bg-warning'
        };
        return statusColors[status as keyof typeof statusColors] || 'bg-secondary';
    };

    const getTypeBadge = (type: string) => {
        const typeColors = {
            'consultation': 'bg-info',
            'procedure': 'bg-warning',
            'emergency': 'bg-danger',
            'routine': 'bg-secondary'
        };
        return typeColors[type as keyof typeof typeColors] || 'bg-secondary';
    };

    const getContractStatusBadge = (status: string) => {
        const contractColors = {
            'draft': 'bg-secondary',
            'pending': 'bg-warning',
            'signed': 'bg-success',
            'expired': 'bg-danger'
        };
        return contractColors[status as keyof typeof contractColors] || 'bg-secondary';
    };

    const filteredCollaborations = collaborations.filter(collab => {
        const matchesSearch = collab.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            collab.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            collab.patient.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || collab.status === filterStatus;
        const matchesType = filterType === 'all' || collab.collaboration_type === filterType;
        return matchesSearch && matchesStatus && matchesType;
    });

    const getOverallStats = () => {
        const total = collaborations.length;
        const active = collaborations.filter(c => c.status === 'active').length;
        const completed = collaborations.filter(c => c.status === 'completed').length;
        const totalRevenue = collaborations.reduce((sum, c) => sum + c.billing.total_amount, 0);
        const totalTasks = collaborations.reduce((sum, c) => sum + c.stats.tasks_count, 0);
        const completedTasks = collaborations.reduce((sum, c) => sum + c.stats.completed_tasks, 0);

        return {
            total,
            active,
            completed,
            totalRevenue,
            totalTasks,
            completedTasks,
            completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
            taskCompletionRate: totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0
        };
    };

    const stats = getOverallStats();

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    // Render different views based on activeView state
    if (activeView === 'telehealth' && selectedCollaboration) {
        return (
            <TelehealthInterface
                collaborationId={selectedCollaboration.collaboration_id}
                onClose={() => setActiveView('overview')}
            />
        );
    }

    if (activeView === 'documents' && selectedCollaboration) {
        return (
            <DocumentManagement
                collaborationId={selectedCollaboration.collaboration_id}
                onClose={() => setActiveView('overview')}
            />
        );
    }

    if (activeView === 'tasks' && selectedCollaboration) {
        return (
            <TaskManagement
                collaborationId={selectedCollaboration.collaboration_id}
                onClose={() => setActiveView('overview')}
            />
        );
    }

    if (activeView === 'contracts' && selectedCollaboration) {
        return (
            <ContractsPanel
                collaborationId={selectedCollaboration.collaboration_id}
                onClose={() => setActiveView('overview')}
            />
        );
    }

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className={"vertical-overlay"} />
            <div className={"main-content"}>
                <div className={"page-content"}>
                    <div className="container-fluid">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="mb-1">
                        <FontAwesomeIcon icon={faHandshake} className="me-3 text-primary" />
                        Collaborations Center
                    </h3>
                    <p className="text-muted mb-0">Manage active healthcare collaborations, telehealth sessions, and patient care workflows</p>
                </div>
                <div>
                    <Link 
                        to="/institution/collaborations"
                        className="btn btn-outline-primary me-2"
                    >
                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                        Create Service Request
                    </Link>
                    {onClose && (
                        <button className="btn btn-outline-secondary" onClick={onClose}>
                            Close
                        </button>
                    )}
                </div>
            </div>

            {/* Statistics Overview */}
            <div className="row mb-4">
                <div className="col-xl-3 col-md-6 mb-4">
                    <div className="card border-left-primary shadow h-100 py-2">
                        <div className="card-body">
                            <div className="row no-gutters align-items-center">
                                <div className="col mr-2">
                                    <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                                        Active Collaborations
                                    </div>
                                    <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.active}</div>
                                </div>
                                <div className="col-auto">
                                    <FontAwesomeIcon icon={faHandshake} className="fa-2x text-gray-300" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-md-6 mb-4">
                    <div className="card border-left-success shadow h-100 py-2">
                        <div className="card-body">
                            <div className="row no-gutters align-items-center">
                                <div className="col mr-2">
                                    <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                                        Completion Rate
                                    </div>
                                    <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.completionRate}%</div>
                                </div>
                                <div className="col-auto">
                                    <FontAwesomeIcon icon={faCheckCircle} className="fa-2x text-gray-300" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-md-6 mb-4">
                    <div className="card border-left-info shadow h-100 py-2">
                        <div className="card-body">
                            <div className="row no-gutters align-items-center">
                                <div className="col mr-2">
                                    <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                                        Task Completion
                                    </div>
                                    <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.taskCompletionRate}%</div>
                                </div>
                                <div className="col-auto">
                                    <FontAwesomeIcon icon={faTasks} className="fa-2x text-gray-300" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-md-6 mb-4">
                    <div className="card border-left-warning shadow h-100 py-2">
                        <div className="card-body">
                            <div className="row no-gutters align-items-center">
                                <div className="col mr-2">
                                    <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                                        Total Revenue
                                    </div>
                                    <div className="h5 mb-0 font-weight-bold text-gray-800">${stats.totalRevenue.toLocaleString()}</div>
                                </div>
                                <div className="col-auto">
                                    <FontAwesomeIcon icon={faDollarSign} className="fa-2x text-gray-300" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search and Filters */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-4">
                            <div className="input-group">
                                <span className="input-group-text">
                                    <FontAwesomeIcon icon={faSearch} />
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search collaborations..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <select
                                className="form-select"
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                            >
                                <option value="all">All Status</option>
                                <option value="active">Active</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="on_hold">On Hold</option>
                            </select>
                        </div>
                        <div className="col-md-3">
                            <select
                                className="form-select"
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                            >
                                <option value="all">All Types</option>
                                <option value="consultation">Consultation</option>
                                <option value="procedure">Procedure</option>
                                <option value="emergency">Emergency</option>
                                <option value="routine">Routine</option>
                            </select>
                        </div>
                        <div className="col-md-2">
                            <button className="btn btn-outline-secondary w-100">
                                <FontAwesomeIcon icon={faFilter} className="me-2" />
                                Filter
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Collaborations Grid */}
            <div className="row">
                {filteredCollaborations.map((collab) => (
                    <div key={collab.collaboration_id} className="col-lg-6 mb-4">
                        <div className="card h-100">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center gap-2">
                                    <span className={`badge ${getStatusBadge(collab.status)}`}>
                                        {collab.status.toUpperCase()}
                                    </span>
                                                                                <span className={`badge ${getTypeBadge(collab.collaboration_type)}`}>
                                                {collab.collaboration_type.toUpperCase()}
                                            </span>
                                </div>
                                <div className="dropdown">
                                    <button className="btn btn-sm btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown">
                                        <FontAwesomeIcon icon={faCog} />
                                    </button>
                                    <ul className="dropdown-menu">
                                        <li>
                                            <button 
                                                className="dropdown-item"
                                                onClick={() => {
                                                    setSelectedCollaboration(collab);
                                                    setActiveView('details');
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faEye} className="me-2" />
                                                View Details
                                            </button>
                                        </li>
                                        <li>
                                            <button 
                                                className="dropdown-item"
                                                onClick={() => {
                                                    setSelectedCollaboration(collab);
                                                    setActiveView('telehealth');
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faVideo} className="me-2" />
                                                Telehealth
                                            </button>
                                        </li>
                                        <li>
                                            <button 
                                                className="dropdown-item"
                                                onClick={() => {
                                                    setSelectedCollaboration(collab);
                                                    setActiveView('documents');
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faFile} className="me-2" />
                                                Documents
                                            </button>
                                        </li>
                                        <li>
                                            <button 
                                                className="dropdown-item"
                                                onClick={() => {
                                                    setSelectedCollaboration(collab);
                                                    setActiveView('tasks');
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faTasks} className="me-2" />
                                                Tasks
                                            </button>
                                        </li>
                                        <li>
                                            <button
                                                className="dropdown-item"
                                                onClick={() => {
                                                    setSelectedCollaboration(collab);
                                                    setActiveView('contracts');
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faFileContract} className="me-2" />
                                                Contracts
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div className="card-body">
                                <h6 className="card-title">{collab.title}</h6>
                                <p className="card-text text-muted small">{collab.description}</p>
                                
                                <div className="mb-3">
                                    <small className="text-muted">Patient:</small>
                                    <div className="fw-bold">{collab.patient.name}</div>
                                    <small className="text-muted">{collab.patient.contact}</small>
                                </div>

                                <div className="mb-3">
                                    <small className="text-muted">Created by:</small>
                                    <div className="fw-bold">{collab.created_by}</div>
                                </div>

                                <div className="row mb-3">
                                    <div className="col-6">
                                        <small className="text-muted">Type:</small>
                                        <div>
                                            <span className={`badge ${getTypeBadge(collab.collaboration_type)}`}>
                                                {collab.collaboration_type.toUpperCase()}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <small className="text-muted">Progress:</small>
                                        <div className="progress" style={{ height: '20px' }}>
                                            <div 
                                                className="progress-bar" 
                                                style={{ width: `${collab.progress_percentage}%` }}
                                            >
                                                {collab.progress_percentage}%
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-6">
                                        <small className="text-muted">Billing:</small>
                                        <div className="fw-bold text-success">
                                            {collab.billing.currency} {collab.billing.total_amount}
                                        </div>
                                        <small className="text-muted">
                                            {collab.billing.billing_mode.replace('_', ' ')}
                                        </small>
                                    </div>
                                    <div className="col-6">
                                        <small className="text-muted">Payment:</small>
                                        <div>
                                            <span className={`badge ${
                                                collab.billing.payment_status === 'completed' ? 'bg-success' : 
                                                collab.billing.payment_status === 'partial' ? 'bg-warning' : 'bg-secondary'
                                            }`}>
                                                {collab.billing.payment_status.toUpperCase()}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="card-footer">
                                <div className="row">
                                    <div className="col-4 text-center">
                                        <small className="text-muted">Tasks</small>
                                        <div className="fw-bold">{collab.stats.tasks_count}</div>
                                    </div>
                                    <div className="col-4 text-center">
                                        <small className="text-muted">Documents</small>
                                        <div className="fw-bold">{collab.stats.documents_count}</div>
                                    </div>
                                    <div className="col-4 text-center">
                                        <small className="text-muted">Sessions</small>
                                        <div className="fw-bold">{collab.stats.telehealth_sessions}</div>
                                    </div>
                                </div>
                                <div className="mt-2">
                                    <small className="text-muted">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                                        {new Date(collab.start_date).toLocaleDateString()} - {new Date(collab.end_date).toLocaleDateString()}
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {filteredCollaborations.length === 0 && (
                <div className="text-center py-5">
                    <FontAwesomeIcon icon={faHandshake} size="3x" className="text-muted mb-3" />
                    <h5>No Collaborations Found</h5>
                    <p className="text-muted">Create your first collaboration to get started</p>
                </div>
            )}

            {/* Create Collaboration Modal - Removed, use Service Center instead */}
            {false && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                                    Create New Collaboration
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close"
                                    onClick={() => {}}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-8">
                                        <div className="mb-3">
                                            <label className="form-label">Collaboration Title *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value=""
                                                onChange={(e) => {}}
                                                placeholder="Enter collaboration title"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="mb-3">
                                            <label className="form-label">Type</label>
                                            <select
                                                className="form-select"
                                                value="consultation"
                                                onChange={(e) => {}}
                                            >
                                                <option value="consultation">Consultation</option>
                                                <option value="procedure">Procedure</option>
                                                <option value="emergency">Emergency</option>
                                                <option value="routine">Routine</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="mb-3">
                                    <label className="form-label">Description *</label>
                                    <textarea
                                        className="form-control"
                                        rows={3}
                                        value=""
                                        onChange={(e) => {}}
                                        placeholder="Enter collaboration description"
                                        required
                                    />
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">Patient ID *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value=""
                                                onChange={(e) => {}}
                                                placeholder="Enter patient ID"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">Billing Mode</label>
                                            <select
                                                className="form-select"
                                                value="per_request"
                                                onChange={(e) => {}}
                                            >
                                                <option value="per_person">Per Person</option>
                                                <option value="per_request">Per Request</option>
                                                <option value="per_milestone">Per Milestone</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-4">
                                        <div className="mb-3">
                                            <label className="form-label">Start Date *</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value=""
                                                onChange={(e) => {}}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="mb-3">
                                            <label className="form-label">End Date *</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value=""
                                                onChange={(e) => {}}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="mb-3">
                                            <label className="form-label">Total Amount</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value=""
                                                onChange={(e) => {}}
                                                placeholder="0.00"
                                                step="0.01"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary"
                                    onClick={() => {}}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-primary"
                                    onClick={() => {}}
                                >
                                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                                    Create Collaboration
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Collaboration Details Modal */}
            {activeView === 'details' && selectedCollaboration && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-xl">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <FontAwesomeIcon icon={faHandshake} className="me-2" />
                                    Collaboration Details: {selectedCollaboration.title}
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close"
                                    onClick={() => setActiveView('overview')}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-8">
                                        <div className="card mb-3">
                                            <div className="card-header">
                                                <h6 className="mb-0">Collaboration Information</h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <p><strong>Type:</strong> {selectedCollaboration.collaboration_type.replace('_', ' ')}</p>
                                                        <p><strong>Status:</strong> 
                                                            <span className={`badge ${getStatusBadge(selectedCollaboration.status)} ms-2`}>
                                                                {selectedCollaboration.status.toUpperCase()}
                                                            </span>
                                                        </p>
                                                        <p><strong>Progress:</strong> {selectedCollaboration.progress_percentage}%</p>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <p><strong>Created:</strong> {new Date(selectedCollaboration.created_at).toLocaleDateString()}</p>
                                                        <p><strong>Start Date:</strong> {new Date(selectedCollaboration.start_date).toLocaleDateString()}</p>
                                                        <p><strong>End Date:</strong> {new Date(selectedCollaboration.end_date).toLocaleDateString()}</p>
                                                    </div>
                                                </div>
                                                
                                                <div className="mb-3">
                                                    <strong>Description:</strong>
                                                    <p className="mt-2">{selectedCollaboration.description}</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="card">
                                            <div className="card-header">
                                                <h6 className="mb-0">Quick Actions</h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-3 text-center">
                                                        <button 
                                                            className="btn btn-outline-primary w-100 mb-2"
                                                            onClick={() => setActiveView('telehealth')}
                                                        >
                                                            <FontAwesomeIcon icon={faVideo} size="2x" className="mb-2" />
                                                            <div>Telehealth</div>
                                                        </button>
                                                    </div>
                                                    <div className="col-md-3 text-center">
                                                        <button 
                                                            className="btn btn-outline-info w-100 mb-2"
                                                            onClick={() => setActiveView('documents')}
                                                        >
                                                            <FontAwesomeIcon icon={faFile} size="2x" className="mb-2" />
                                                            <div>Documents</div>
                                                        </button>
                                                    </div>
                                                    <div className="col-md-3 text-center">
                                                        <button 
                                                            className="btn btn-outline-success w-100 mb-2"
                                                            onClick={() => setActiveView('tasks')}
                                                        >
                                                            <FontAwesomeIcon icon={faTasks} size="2x" className="mb-2" />
                                                            <div>Tasks</div>
                                                        </button>
                                                    </div>
                                                    <div className="col-md-3 text-center">
                                                        <button 
                                                            className="btn btn-outline-warning w-100 mb-2"
                                                        >
                                                            <FontAwesomeIcon icon={faCalendarAlt} size="2x" className="mb-2" />
                                                            <div>Calendar</div>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="col-md-4">
                                        <div className="card mb-3">
                                            <div className="card-header">
                                                <h6 className="mb-0">Patient Information</h6>
                                            </div>
                                            <div className="card-body">
                                                <p><strong>Name:</strong> {selectedCollaboration.patient.name}</p>
                                                <p><strong>Contact:</strong> {selectedCollaboration.patient.contact}</p>
                                                <p><strong>Email:</strong> {selectedCollaboration.patient.email}</p>
                                            </div>
                                        </div>

                                        <div className="card mb-3">
                                            <div className="card-header">
                                                <h6 className="mb-0">Billing Information</h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="mb-3">
                                                    <strong>Amount:</strong>
                                                    <div className="fw-bold text-success">
                                                        {selectedCollaboration.billing.currency} {selectedCollaboration.billing.total_amount}
                                                    </div>
                                                </div>
                                                <p><strong>Payment Status:</strong> 
                                                    <span className={`badge ${
                                                        selectedCollaboration.billing.payment_status === 'completed' ? 'bg-success' : 
                                                        selectedCollaboration.billing.payment_status === 'partial' ? 'bg-warning' : 'bg-secondary'
                                                    }`}>
                                                        {selectedCollaboration.billing.payment_status.toUpperCase()}
                                                    </span>
                                                </p>
                                                <p><strong>Billing Mode:</strong> {selectedCollaboration.billing.billing_mode.replace('_', ' ')}</p>
                                            </div>
                                        </div>

                                        <div className="card">
                                            <div className="card-header">
                                                <h6 className="mb-0">Statistics</h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="row text-center">
                                                    <div className="col-6">
                                                        <div className="h4 text-primary">{selectedCollaboration.stats.tasks_count}</div>
                                                        <small>Total Tasks</small>
                                                    </div>
                                                    <div className="col-6">
                                                        <div className="h4 text-success">{selectedCollaboration.stats.completed_tasks}</div>
                                                        <small>Completed</small>
                                                    </div>
                                                </div>
                                                <hr />
                                                <div className="row text-center">
                                                    <div className="col-6">
                                                        <div className="h4 text-info">{selectedCollaboration.stats.documents_count}</div>
                                                        <small>Documents</small>
                                                    </div>
                                                    <div className="col-6">
                                                        <div className="h4 text-warning">{selectedCollaboration.stats.telehealth_sessions}</div>
                                                        <small>Sessions</small>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary"
                                    onClick={() => setActiveView('overview')}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
                    </div>
                </div>
            </div>
            <DashboardFooter />
        </>
    );
};

export default CollaborationsCenter;